import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { notify } from '../../notifications';
import {
  MailAuthStatus,
  MailFolderKey,
  MailFolder,
  MailMessageSummary,
  MailMessageDetail
} from './types';
import { MAIL_FOLDERS, MAIL_COLORS } from './constants';
import {
  fetchMailFolders,
  fetchMailMessages,
  fetchMailMessageDetail,
  executeMailAction
} from './api';

import MailAuthRequired from './components/MailAuthRequired';
import MailAuthError from './components/MailAuthError';
import MailFolderList from './components/MailFolderList';
import MailToolbar from './components/MailToolbar';
import MailList from './components/MailList';
import MailViewer from './components/MailViewer';

interface CorporateMailProps {
  username: string;
  onNavigateToProfile: () => void;
}

export const CorporateMail: React.FC<CorporateMailProps> = ({
  username,
  onNavigateToProfile
}) => {
  // ── Состояния авторизации ──────────────────────────────────────────────────
  const [authStatus, setAuthStatus] = useState<MailAuthStatus>('loading');
  const [authErrorMessage, setAuthErrorMessage] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  // ── Состояния папок и писем ───────────────────────────────────────────────
  const [currentFolder, setCurrentFolder] = useState<MailFolderKey>('inbox');
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MailMessageDetail | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // ── Состояния загрузки ─────────────────────────────────────────────────────
  const [isMessagesLoading, setIsMessagesLoading] = useState<boolean>(false);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);

  // ── Пагинация ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const pageSize = 50;

  // ── Поиск и фильтрация ────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterUnreadOnly, setFilterUnreadOnly] = useState<boolean>(false);

  // ── Загрузка папок ────────────────────────────────────────────────────────
  const loadFolders = useCallback(async () => {
    if (!username) return;
    try {
      const fList = await fetchMailFolders(username);
      setFolders(fList);
    } catch {
      // Игнорируем фоновые ошибки обновления папок
    }
  }, [username]);

  // ── Подсчет общего количества писем в текущей папке ────────────────────────
  const currentFolderTotal = useMemo(() => {
    if (searchQuery) return null;
    const match = folders.find(
      (f) => f.name.toLowerCase() === currentFolder || f.path.toLowerCase().includes(currentFolder)
    );
    return match?.totalCount ?? null;
  }, [folders, currentFolder, searchQuery]);

  // ── Загрузка писем текущей папки ──────────────────────────────────────────
  const loadMessages = useCallback(
    async (
      folder: MailFolderKey = currentFolder,
      queryText: string = searchQuery,
      targetPage: number = 0
    ) => {
      if (!username) return;
      setIsMessagesLoading(true);

      try {
        const offset = targetPage * pageSize;
        const res = await fetchMailMessages(username, folder, queryText, pageSize, offset);

        if (res.success) {
          setAuthStatus('authorized');
          if (res.email) setUserEmail(res.email);
          setMessages(res.messages || []);
          setHasMore(Boolean(res.more));
          setPage(targetPage);
        } else {
          if (res.requiresProfileSetup || res.code === 'NO_CREDENTIALS') {
            setAuthStatus('no_credentials');
          } else if (res.code === 'AUTH_FAILED') {
            setAuthStatus('auth_failed');
            setAuthErrorMessage(res.message || 'Ошибка авторизации в корпоративной почте');
          } else {
            setAuthStatus('network_error');
            setAuthErrorMessage(res.message || 'Не удалось подключиться к серверу');
          }
        }
      } catch (err: any) {
        const responseData = err?.response?.data;
        if (responseData?.code === 'NO_CREDENTIALS' || responseData?.requiresProfileSetup) {
          setAuthStatus('no_credentials');
        } else if (responseData?.code === 'AUTH_FAILED') {
          setAuthStatus('auth_failed');
          setAuthErrorMessage(responseData?.message || 'Ошибка авторизации в корпоративной почте');
        } else {
          setAuthStatus('network_error');
          setAuthErrorMessage('Почтовый сервер Carbonio временно недоступен');
        }
      } finally {
        setIsMessagesLoading(false);
      }
    },
    [username, currentFolder, searchQuery, pageSize]
  );

  // ── Первоначальная загрузка ───────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!username) return;
      setAuthStatus('loading');
      await loadMessages('inbox', '', 0);
      if (isMounted) {
        await loadFolders();
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Перелистывание страниц ─────────────────────────────────────────────────
  const handlePrevPage = () => {
    if (page > 0) {
      loadMessages(currentFolder, searchQuery, page - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      loadMessages(currentFolder, searchQuery, page + 1);
    }
  };

  // ── Выбор папки ───────────────────────────────────────────────────────────
  const handleSelectFolder = (folder: MailFolderKey) => {
    setCurrentFolder(folder);
    setSelectedMessage(null);
    setSelectedMessageId(null);
    setPage(0);
    loadMessages(folder, searchQuery, 0);
  };

  // ── Поиск по письмам ──────────────────────────────────────────────────────
  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  // Дебаунс поиск
  useEffect(() => {
    if (authStatus !== 'authorized') return;
    const timer = setTimeout(() => {
      setPage(0);
      loadMessages(currentFolder, searchQuery, 0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Выбор и открытие конкретного письма ────────────────────────────────────
  const handleSelectMessage = async (msg: MailMessageSummary) => {
    setSelectedMessageId(msg.id);
    setIsDetailLoading(true);

    // Локально сразу отмечаем как прочитанное
    if (msg.isUnread) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, isUnread: false } : m))
      );
      setFolders((prev) =>
        prev.map((f) =>
          f.name.toLowerCase() === currentFolder || f.path.toLowerCase().includes(currentFolder)
            ? { ...f, unreadCount: Math.max(0, f.unreadCount - 1) }
            : f
        )
      );
    }

    try {
      const detail = await fetchMailMessageDetail(username, msg.id);
      setSelectedMessage(detail);
    } catch {
      notify.error('Не удалось загрузить содержимое письма');
    } finally {
      setIsDetailLoading(false);
    }
  };

  // ── Действие: Пометить как непрочитанное ──────────────────────────────────
  const handleMarkUnread = async (id: string) => {
    try {
      await executeMailAction(username, id, '!read');
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, isUnread: true } : m))
      );
      setFolders((prev) =>
        prev.map((f) =>
          f.name.toLowerCase() === currentFolder || f.path.toLowerCase().includes(currentFolder)
            ? { ...f, unreadCount: f.unreadCount + 1 }
            : f
        )
      );
      setSelectedMessage(null);
      setSelectedMessageId(null);
      notify.info('Письмо помечено как непрочитанное');
    } catch {
      notify.error('Ошибка при изменении статуса письма');
    }
  };

  // ── Действие: Удалить в корзину ───────────────────────────────────────────
  const handleDeleteMessage = async (id: string) => {
    try {
      await executeMailAction(username, id, 'trash');
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setSelectedMessage(null);
      setSelectedMessageId(null);
      notify.info('Письмо перемещено в корзину');
      loadFolders();
    } catch {
      notify.error('Ошибка при удалении письма');
    }
  };

  // ── Ручное обновление ─────────────────────────────────────────────────────
  const handleRefresh = () => {
    loadMessages(currentFolder, searchQuery, page);
    loadFolders();
  };

  // ── Фильтрация списка писем ───────────────────────────────────────────────
  const displayedMessages = useMemo(() => {
    if (!filterUnreadOnly) return messages;
    return messages.filter((m) => m.isUnread);
  }, [messages, filterUnreadOnly]);

  const currentFolderDef = MAIL_FOLDERS.find((f) => f.key === currentFolder);

  // ── 1. Состояние: Загрузка / проверка авторизации ──────────────────────────
  if (authStatus === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}
      >
        <CircularProgress size={42} sx={{ color: MAIL_COLORS.primary }} />
        <Typography variant="body1" sx={{ color: '#475569', fontWeight: 500 }}>
          Проверка авторизации в корпоративной почте...
        </Typography>
      </Box>
    );
  }

  // ── 2. Состояние: Нет учетных данных (переход в профиль) ───────────────────
  if (authStatus === 'no_credentials') {
    return <MailAuthRequired onNavigateToProfile={onNavigateToProfile} />;
  }

  // ── 3. Состояние: Ошибка авторизации (неверный пароль и т.д.) ──────────────
  if (authStatus === 'auth_failed' || authStatus === 'network_error') {
    return (
      <MailAuthError
        message={authErrorMessage}
        onRetry={() => loadMessages(currentFolder, searchQuery, 0)}
        onNavigateToProfile={onNavigateToProfile}
      />
    );
  }

  // ── 4. Состояние: Авторизован, отображение почтового интерфейса ────────────
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden'
      }}
    >
      {/* 1. Левая колонка: Список папок */}
      <MailFolderList
        currentFolder={currentFolder}
        folders={folders}
        userEmail={userEmail}
        onSelectFolder={handleSelectFolder}
      />

      {/* 2. Правая часть: Тулбар + Двухколоночный интерфейс (Список писем + Просмотр) */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Тулбар (поиск, фильтр, обновление, пагинация) */}
        <MailToolbar
          currentFolderLabel={currentFolderDef?.label || 'Входящие'}
          totalMessagesCount={displayedMessages.length}
          totalCount={currentFolderTotal}
          page={page}
          pageSize={pageSize}
          hasMore={hasMore}
          searchQuery={searchQuery}
          filterUnreadOnly={filterUnreadOnly}
          isLoading={isMessagesLoading}
          onSearchChange={handleSearchChange}
          onToggleUnreadOnly={() => setFilterUnreadOnly((v) => !v)}
          onRefresh={handleRefresh}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
        />

        {/* Рабочая область: Список писем (слева) + Просмотр (справа) */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          {/* Колонка списка писем */}
          <Box
            sx={{
              width: { xs: selectedMessage ? '0%' : '100%', lg: '380px', xl: '420px' },
              display: { xs: selectedMessage ? 'none' : 'block', lg: 'block' },
              flexShrink: 0,
              borderRight: `1px solid ${MAIL_COLORS.borderLight}`,
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <MailList
              messages={displayedMessages}
              selectedMessageId={selectedMessageId}
              isLoading={isMessagesLoading}
              hasFilterActive={Boolean(searchQuery || filterUnreadOnly)}
              page={page}
              hasMore={hasMore}
              pageSize={pageSize}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onSelectMessage={handleSelectMessage}
            />
          </Box>

          {/* Колонка просмотра выбранного письма */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              height: '100%',
              overflow: 'hidden',
              display: { xs: selectedMessage ? 'block' : 'none', lg: 'block' }
            }}
          >
            <MailViewer
              message={selectedMessage}
              username={username}
              isLoading={isDetailLoading}
              onMarkUnread={handleMarkUnread}
              onDeleteMessage={handleDeleteMessage}
              onClose={() => {
                setSelectedMessage(null);
                setSelectedMessageId(null);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CorporateMail;
